import type { DataSourceLoadOptions } from '@sleed/bizcommerce-api';

export type SortDir = 'asc' | 'desc';

/** Active grid sort: a column `key` (the grid's own column id) + direction. */
export interface GridSort {
	key: string;
	dir: SortDir;
}

/**
 * Assigns the DevExtreme `sort` envelope as a JSON STRING.
 *
 * The generated Axios client serialises a real array-of-objects by flattening it to
 * `sort.selector=…&sort.desc=…`, which the backend `DataSourceLoadOptionsParser` cannot read.
 * A string value is instead passed through verbatim as `sort=<json>` and the parser
 * JSON-deserialises it into `SortingInfo[]`. This is the same contract the `filter` field
 * relies on (see use-products-list / use-orders-list).
 *
 * @param selector backend sort selector — a ProductGridProjection property name (Products,
 *   no remap) or a legacy short key remapped by the OrdersController allowlist.
 */
export function applySort(loadOptions: DataSourceLoadOptions, selector: string, dir: SortDir): void {
	(loadOptions as Record<string, unknown>).sort = JSON.stringify([{ selector, desc: dir === 'desc' }]);
}

/** Header-click cycle: unsorted → asc → desc → unsorted (mirrors SystemGrid.cycleSort). */
export function cycleSort(prev: GridSort | null, key: string): GridSort | null {
	if (prev?.key !== key) return { key, dir: 'asc' };
	if (prev.dir === 'asc') return { key, dir: 'desc' };
	return null;
}

/** Reads `{ key, dir }` from URL params (`sort` + `dir`), or null when no/blank sort. */
export function sortFromParams(params: URLSearchParams): GridSort | null {
	const key = params.get('sort');
	if (!key) return null;
	return { key, dir: params.get('dir') === 'desc' ? 'desc' : 'asc' };
}

/** Writes (or clears) the `sort`/`dir` URL params from a {@link GridSort}. */
export function writeSortParams(params: URLSearchParams, sort: GridSort | null): void {
	if (sort) {
		params.set('sort', sort.key);
		params.set('dir', sort.dir);
	} else {
		params.delete('sort');
		params.delete('dir');
	}
}
